import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocketContext } from '../contexts/WebSocketProvider';

export default function useCreateAdmin() {
  const { send, isConnected, isOpen, connect } = useWebSocketContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  const createAdmin = useCallback(
    async (adminData) => {
      if (!mountedRef.current) return { success: false, message: "Unmounted" };

      setIsLoading(true);
      setError('');

      try {
        if (!isConnected) {
          if (!connectionAttemptedRef.current) {
            connectionAttemptedRef.current = true;
            await connect();
          }

          // Wait for connection with timeout
          await new Promise((resolve, reject) => {
            const start = Date.now();
            const interval = setInterval(() => {
              if (!mountedRef.current) {
                clearInterval(interval);
                reject(new Error('Component unmounted'));
              } else if (isOpen()) {
                clearInterval(interval);
                resolve(true);
              } else if (Date.now() - start > 10000) {
                clearInterval(interval);
                reject(new Error('Connection timeout'));
              }
            }, 100);
          });
        }

        const response = await send('cms_register_user', {
          email: adminData.email,
          phone: adminData.phone,
          name: adminData.name,
          password: adminData.password,
          group_id: adminData.group_id,
          role_id: adminData.role_id,
          admin_role_id: Number(adminData.admin_role_id),
        });

        console.log("Create admin response:", response);

        if (!response || response.status === 'error' || response.success === false) {
          throw new Error(response?.message || 'Failed to create admin');
        }

        return { success: true, message: response.message || "Admin created successfully", data: response };
      } catch (err) {
        if (mountedRef.current) setError(err.message);
        return { success: false, message: err.message };
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, isOpen, connect, send]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { createAdmin, isLoading, error };
}